import Papa from 'papaparse';

export interface CsvData {
  headers: string[];
  rows: string[][];
  delimiter: string;
  hasHeaders: boolean;
  totalRows: number;
}

export interface CsvDetectionResult {
  isCSV: boolean;
  data?: CsvData;
  error?: string;
}

/**
 * Detects if a string contains CSV data and parses it
 * @param value - String value to check for CSV data
 * @param minRows - Minimum number of rows required to consider it CSV (default: 2)
 * @param minColumns - Minimum number of columns required to consider it CSV (default: 2)
 * @returns CsvDetectionResult with detection status and parsed data
 */
export function detectCSV(value: string, minRows: number = 2, minColumns: number = 2): CsvDetectionResult {
  if (!value || typeof value !== 'string') {
    return { isCSV: false, error: 'Invalid input' };
  }

  // Quick check: CSV-like patterns
  const trimmedValue = value.trim();
  if (!trimmedValue.includes(',') && !trimmedValue.includes('\t')) {
    return { isCSV: false, error: 'No delimiters found' };
  }

  try {
    // Try parsing with Papa.parse
    const parseResult = Papa.parse(trimmedValue, {
      header: false,
      skipEmptyLines: true,
      delimiter: '', // Auto-detect delimiter
    });

    if (parseResult.errors && parseResult.errors.length > 0) {
      return {
        isCSV: false,
        error: `Parse error: ${parseResult.errors[0].message}`,
      };
    }

    const rows = parseResult.data as string[][];

    if (!rows || rows.length < minRows) {
      return {
        isCSV: false,
        error: `Insufficient rows: ${rows?.length || 0} < ${minRows}`,
      };
    }

    // Check if all rows have consistent column count
    const firstRowLength = rows[0]?.length || 0;
    if (firstRowLength < minColumns) {
      return {
        isCSV: false,
        error: `Insufficient columns: ${firstRowLength} < ${minColumns}`,
      };
    }

    const consistentColumns = rows.every(
      (row) => row.length === firstRowLength || Math.abs(row.length - firstRowLength) <= 1 // Allow 1 column difference for flexibility
    );

    if (!consistentColumns) {
      return {
        isCSV: false,
        error: 'Inconsistent column count across rows',
      };
    }

    // Detect if first row is likely headers
    const hasHeaders = detectHeaders(rows);

    const headers = hasHeaders ? rows[0] : [];
    const dataRows = hasHeaders ? rows.slice(1) : rows;

    return {
      isCSV: true,
      data: {
        headers,
        rows: dataRows,
        delimiter: parseResult.meta.delimiter || ',',
        hasHeaders,
        totalRows: rows.length,
      },
    };
  } catch (error) {
    return {
      isCSV: false,
      error: `Parsing failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Heuristic to detect if the first row contains headers
 * @param rows - Parsed CSV rows
 * @returns boolean indicating if first row is likely headers
 */
function detectHeaders(rows: string[][]): boolean {
  if (rows.length < 2) return false;

  const firstRow = rows[0];
  const secondRow = rows[1];

  if (!firstRow || !secondRow) return false;

  // Check if first row has mostly non-numeric values while second row has more numeric values
  const firstRowNumeric = firstRow.filter((cell) => !isNaN(Number(cell.trim()))).length;
  const secondRowNumeric = secondRow.filter((cell) => !isNaN(Number(cell.trim()))).length;

  // If first row is mostly non-numeric and second row is more numeric, likely headers
  if (firstRowNumeric < firstRow.length / 2 && secondRowNumeric > firstRowNumeric) {
    return true;
  }

  // Check for common header patterns (length, special characters, etc.)
  const hasHeaderPattern = firstRow.some((cell) => {
    const trimmed = cell.trim().toLowerCase();
    return (
      trimmed.includes('_') ||
      trimmed.includes(' ') ||
      trimmed.length > 10 || // Long descriptive names
      /^[a-z]+$/i.test(trimmed)
    ); // Pure alphabetic
  });

  return hasHeaderPattern;
}
